import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { RootState } from 'store/reducers';
import { affiliateSignup, AffiliateSignupPayload } from 'store/actions/auth.action';
import { isInValidPassword, isUserLoggedIn } from 'utils/handler';
import AffiliateIdCard from 'views/registrar-integrations/AffiliateIdCard';
import FormPageLayout from 'views/registrar-integrations/FormPageLayout';

const BENEFITS = [
  'Offer expert brokers on every taken domain your customers search',
  'Earn commission on every customer you send us',
  'Track clicks, sales and commission in your dashboard',
  'Free WHMCS addon, set up in minutes',
];

export const AffiliatesPage: React.FC = (): JSX.Element => {
  const { localCountryStr } = useSelector((state: RootState) => state.mainReducer);
  const [affiliate, setAffiliate] = useState<{ affiliateId: string; email: string }>();
  const [serverError, setServerError] = useState('');
  const {
    control,
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<AffiliateSignupPayload>({ mode: 'onSubmit' });

  const onSubmit: SubmitHandler<AffiliateSignupPayload> = async (data) => {
    setServerError('');
    const { affiliateId, error } = await affiliateSignup(data);
    if (affiliateId) {
      setAffiliate({ affiliateId, email: data.email });
    } else {
      setServerError(error || '');
    }
  };

  if (isUserLoggedIn()) return <Redirect to="/dashboard" />;

  return (
    <FormPageLayout
      title="Become a Saw.com Affiliate"
      heading="Become a Saw.com affiliate"
      subtitle="Sign up to get your Affiliate ID instantly and offer our domain brokers to your customers."
      intro={
        <ul className="affiliate-benefits">
          {BENEFITS.map((benefit) => <li key={benefit}>{benefit}</li>)}
        </ul>
      }
    >
      {affiliate ? <AffiliateIdCard {...affiliate} /> : (
        <div className="rewamp-form">
          <form className="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-group">
              <input type="text" autoComplete="organization" placeholder="Company Name*" {...register('name', {
                setValueAs: (value: string) => value.trim(),
                required: 'Company name is required',
                maxLength: { value: 100, message: 'Company name is too long' },
              })} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </div>

            <div className="form-group">
              <input type="text" autoComplete="street-address" placeholder="Company Address*" {...register('address', {
                setValueAs: (value: string) => value.trim(),
                required: 'Company address is required',
                maxLength: { value: 255, message: 'Address is too long' },
              })} />
              {errors.address && <span className="field-error">{errors.address.message}</span>}
            </div>

            <div className="form-group">
              <input type="text" inputMode="url" autoComplete="url" placeholder="Company Website*" {...register('website', {
                required: 'Website is required',
                pattern: { value: /^(https?:\/\/)?[^\s/]+\.[^\s]+$/i, message: 'Enter a website like example.com' },
              })} />
              {errors.website && <span className="field-error">{errors.website.message}</span>}
            </div>

            <div className="form-group">
              <input type="email" autoComplete="email" placeholder="Company Email*" {...register('email', {
                setValueAs: (value: string) => value.trim(),
                required: 'Email is required',
                pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
              })} />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>

            <div className="form-group new-phone-input">
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: 'Phone number is required',
                  validate: (value) => isValidPhoneNumber(value || '') || 'Invalid phone number',
                }}
                render={({ field }) => (
                  <PhoneInput
                    name={field.name}
                    international
                    defaultCountry={localCountryStr}
                    placeholder="Company Phone*"
                    value={field.value}
                    onChange={field.onChange}
                    className="form-control"
                  />
                )}
              />
              {errors.phone && <span className="field-error">{errors.phone.message}</span>}
            </div>

            <div className="form-group">
              <input type="password" autoComplete="new-password" placeholder="Password*" {...register('password', {
                required: 'Password is required',
                validate: (value) => !isInValidPassword(value) || 'At least 8 characters, with a number, an uppercase letter and a special character',
              })} />
              {errors.password && <span className="field-error">{errors.password.message}</span>}
            </div>

            <div className="form-group">
              <input type="password" autoComplete="new-password" placeholder="Confirm Password*" {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === getValues('password') || 'Passwords do not match',
              })} />
              {errors.confirmPassword && <span className="field-error">{errors.confirmPassword.message}</span>}
            </div>

            <div className="form-group">
              <div className="checkbox">
                <input type="checkbox" id="affiliate-terms" {...register('termsAccepted', { required: 'You must accept the terms' })} />
                <label htmlFor="affiliate-terms">
                  By ticking this box I agree that I have read and consent to the{' '}
                  <a href="/affiliates/terms" target="_blank" rel="noopener noreferrer">Affiliate Terms of Use</a> and{' '}
                  <a href="https://saw.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </label>
              </div>
              {errors.termsAccepted && <span className="field-error">{errors.termsAccepted.message}</span>}
            </div>

            {serverError && <span className="field-error" role="alert">{serverError}</span>}
            <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Creating account...' : 'Get my Affiliate ID'}</button>
          </form>
          <p className="form-page-switch">Already an affiliate? <Link to="/affiliates/login">Log in</Link></p>
        </div>
      )}
    </FormPageLayout>
  );
};

export default AffiliatesPage;
